import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { z } from "zod";

export type TeamInvitationJSON = z.infer<typeof TeamInvitationSchema.TeamInvitationJSONZ>;
export type TeamInvitationParams = z.infer<typeof TeamInvitationSchema.TeamInvitationZ>;

export default class TeamInvitationSchema {
  public static readonly TeamInvitationJSONZ = z.object({
    id: z.uuid(),
    inviteeId: z.uuid(),
    teamId: z.uuid(),
    message: z.string().nullish(),
    acceptedAt: z.iso.datetime({ offset: true }).nullish(),
    closed: z.boolean(),
  });

  public static readonly TeamInvitationZ = z.object({
    id: z.instanceof(Guid),
    inviteeId: z.instanceof(Guid),
    teamId: z.instanceof(Guid),
    message: z.string().optional(),
    acceptedAt: DateSchema.type.optional(),
    closed: z.boolean(),
  });

  public static validate(values: unknown): TeamInvitationParams {
    return parseOrThrow(TeamInvitationSchema.TeamInvitationZ, values, {
      logError: true,
      schemaName: "TeamInvitationSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): TeamInvitationJSON {
    return parseOrThrow(TeamInvitationSchema.TeamInvitationJSONZ, json, {
      logError: true,
      schemaName: "TeamInvitationSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static validateJSONArray(values: unknown): TeamInvitationJSON[] {
    return parseOrThrow(z.array(TeamInvitationSchema.TeamInvitationJSONZ), values, {
      logError: true,
      schemaName: "TeamInvitationSchema",
      operation: "validateJSONArray",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is TeamInvitationParams {
    try {
      TeamInvitationSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is TeamInvitationJSON {
    try {
      TeamInvitationSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: TeamInvitationJSON): TeamInvitationParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(TeamInvitationSchema.TeamInvitationJSONZ, json, {
      logError: true,
      schemaName: "TeamInvitationSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return TeamInvitationSchema.codec.decode(parsed);
  }

  public static serialize(params: TeamInvitationParams): TeamInvitationJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = TeamInvitationSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return TeamInvitationSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    TeamInvitationSchema.TeamInvitationJSONZ,
    TeamInvitationSchema.TeamInvitationZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        inviteeId: new Guid(GuidSchema.deserialize(data.inviteeId)),
        teamId: new Guid(GuidSchema.deserialize(data.teamId)),
        message: data.message ?? undefined,
        acceptedAt: AHDate.fromJSON(data.acceptedAt) ?? undefined,
        closed: data.closed,
      }),
      encode: (invite) => ({
        id: invite.id.toJSON(),
        inviteeId: invite.inviteeId.toJSON(),
        teamId: invite.teamId.toJSON(),
        message: invite.message,
        acceptedAt: invite.acceptedAt?.toJSON(),
        closed: invite.closed,
      }),
    }
  );
}
